import { apiClient, BASE_URL, HttpError } from './apiClient';

interface AcceoConnectLoginResponse {
    access_token: string;
    token_type: string;
    expires_in: number;
    saasSessionToken: string | null;
}

export interface Tenant {
    tenantCode: string;
    companyName: string;
    username: string;
    ciu: string;
}

export interface TenantList {
    tenants: Tenant[];
    /**
     * The list is known to be incomplete because AcceoConnect could not be reached. Only a local-dev
     * or preview stack ever returns this — a deployed backend fails the request instead of answering
     * with a partial list — and only when that stack declares a real Connect tenant that is therefore
     * missing. Absent means "the list is everything the user has access to".
     */
    connectUnavailable: boolean;
}

/**
 * Fetches all AcceoConnect companies the SSO user has access to, along with whether the list is
 * complete. Sends the Identity Server access token as a Bearer header; the backend validates it
 * against IS before proxying the request to AcceoConnect.
 */
export async function getSSOTenantsResult(isToken: string): Promise<TenantList> {
    const response = await fetch(`${BASE_URL}/connect/sso/tenants`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${isToken}` },
    });
    if (!response.ok) throw new HttpError(response.status, `getSSOTenants failed: ${response.statusText}`);
    const data = await response.json() as Partial<TenantList>;
    return { tenants: data.tenants ?? [], connectUnavailable: data.connectUnavailable === true };
}

/**
 * The tenant list alone, for callers that are matching a tenant they already chose and have nothing to
 * tell the user about a partial list.
 */
export async function getSSOTenants(isToken: string): Promise<Tenant[]> {
    return (await getSSOTenantsResult(isToken)).tenants;
}

/**
 * Exchanges an IS access token + tenant selection for an AcceoConnect bearer token.
 * The IS token is sent as a Bearer header; tenantCode and username are in the body.
 */
export async function loginWithSso(
    isToken: string,
    tenantCode: string,
    username: string,
    ciu: string,
): Promise<AcceoConnectLoginResponse> {
    const response = await fetch(`${BASE_URL}/connect/sso/login`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${isToken}`,
        },
        body: JSON.stringify({ tenantCode, username, ciu }),
    });
    if (!response.ok) throw new HttpError(response.status, `loginWithSso failed: ${response.statusText}`);
    return response.json() as Promise<AcceoConnectLoginResponse>;
}

export async function getCurrentUser(): Promise<{ displayName: string | null } | null> {
    try {
        return await apiClient.get<{ displayName: string | null }>('/connect/me');
    } catch {
        return null;
    }
}

/**
 * Silently refreshes the AcceoConnect token using an existing OIDC session.
 * Fetches the tenant list, matches the stored tenantCode, then calls loginWithSso.
 * Returns null if the tenant is not found in the list.
 */
export async function refreshAcceoConnectToken(
    oidcToken: string,
    tenantCode: string,
): Promise<AcceoConnectLoginResponse | null> {
    const tenants = await getSSOTenants(oidcToken);
    const tenant = tenants.find(t => t.tenantCode === tenantCode);
    if (!tenant) return null;
    return loginWithSso(oidcToken, tenant.tenantCode, tenant.username, tenant.ciu);
}
